import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; });
}

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const GUIDES = {
  ios_safari: { label: "iPhone o iPad · Safari", steps: ["Tocca Condividi (il quadrato con la freccia verso l'alto).", "Scegli «Aggiungi alla schermata Home».", "Tocca «Aggiungi»."] },
  ios_other: { label: "iPhone o iPad · altro browser", steps: ["Apri questa pagina con Safari: è il modo più affidabile su iPhone e iPad.", "In Safari tocca Condividi (il quadrato con la freccia verso l'alto).", "Scegli «Aggiungi alla schermata Home» e tocca «Aggiungi»."] },
  android_chrome: { label: "Android · Chrome", steps: ["Tocca il menu ⋮ in alto a destra.", "Scegli «Installa app» oppure «Aggiungi a schermata Home».", "Conferma."] },
  android_samsung: { label: "Android · Samsung Internet", steps: ["Tocca il menu ≡ in basso a destra.", "Scegli «Aggiungi pagina a» e poi «Schermata Home».", "Conferma."] },
  desktop_chrome: { label: "Computer · Chrome", steps: ["Apri il menu ⋮ in alto a destra.", "Scegli «Trasmetti, salva e condividi» e poi «Installa pagina come app…» (nelle versioni meno recenti: «Installa Medico delle Piante…»).", "Conferma con il pulsante «Installa»."] },
  desktop_edge: { label: "Computer · Edge", steps: ["Apri il menu … in alto a destra.", "Scegli «App» e poi «Installa questo sito come app».", "Conferma con il pulsante «Installa»."] },
  desktop_safari: { label: "Mac · Safari", steps: ["Nella barra dei menu scegli «File».", "Scegli «Aggiungi al Dock» (disponibile da macOS Sonoma).", "Conferma con «Aggiungi»."] },
  desktop_firefox: { label: "Computer · Firefox", steps: ["Firefox per computer non permette di creare questo collegamento.", "Apri Medico delle Piante dal tuo smartphone e segui le istruzioni per il tuo telefono.", "Sul computer puoi in alternativa usare Chrome o Edge."] },
};
const CHOICES = ["ios_safari", "android_chrome", "android_samsung", "desktop_chrome", "desktop_edge", "desktop_safari"];

export function detectGuide(ua = navigator.userAgent, touch = navigator.maxTouchPoints || 0) {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touch > 1)) return /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua) ? "ios_other" : "ios_safari";
  const other = /OPR|Opera|YaBrowser|UCBrowser|Vivaldi|DuckDuckGo|Brave|wv\)/i.test(ua) || !!navigator.brave;
  if (/Android/i.test(ua)) {
    if (/SamsungBrowser/i.test(ua)) return "android_samsung";
    return /Chrome\//i.test(ua) && !/EdgA|Firefox/i.test(ua) && !other ? "android_chrome" : null;
  }
  if (/Mobi/i.test(ua) || other) return null;
  if (/Edg\//i.test(ua)) return "desktop_edge";
  if (/Firefox\//i.test(ua)) return "desktop_firefox";
  if (/Chrome\//i.test(ua)) return "desktop_chrome";
  if (/Macintosh/i.test(ua) && /Version\/.*Safari/i.test(ua)) return "desktop_safari";
  return null;
}

export default function InstallHelp({ open, onOpenChange }) {
  const [guide, setGuide] = useState(null);
  const [detected, setDetected] = useState(null);
  const [canPrompt, setCanPrompt] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (!open) return;
    const g = detectGuide();
    setDetected(g);
    setGuide(g);
    setCanPrompt(!!deferredPrompt);
    setInstalled(isStandalone());
  }, [open]);

  const install = async () => {
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanPrompt(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-sm overflow-y-auto bg-[#f8f3e9]" data-testid="install-help-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0D2517]">Come aggiungere Medico delle Piante alla schermata iniziale</DialogTitle>
          <DialogDescription className="text-sm text-[#2D3A30]">Puoi creare un collegamento a Medico delle Piante per aprirlo facilmente, senza dover cercare ogni volta il sito.</DialogDescription>
        </DialogHeader>
        {installed ? (
          <p className="text-sm text-[#15803D]" data-testid="install-help-installed">Stai già aprendo Medico delle Piante dal collegamento sulla schermata iniziale.</p>
        ) : (
          <div className="space-y-4">
            {canPrompt && <button onClick={install} className="btn-primary w-full" data-testid="install-help-native-button"><Download size={16} /> AGGIUNGI ORA</button>}
            {guide ? (
              <div data-testid={`install-help-steps-${guide}`}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#5b6b60]">{GUIDES[guide].label}</p>
                <ol className="list-decimal space-y-2 pl-5 text-sm text-[#2D3A30]">{GUIDES[guide].steps.map((t) => <li key={t}>{t}</li>)}</ol>
              </div>
            ) : (
              <p className="text-sm text-[#2D3A30]" data-testid="install-help-choose">Scegli il tuo dispositivo e il browser che stai usando:</p>
            )}
            {guide && <p className="text-xs text-[#5b6b60]">{detected ? "Non è il tuo dispositivo? Scegli quello giusto:" : "Oppure scegli un altro dispositivo:"}</p>}
            <div className="flex flex-wrap gap-2" data-testid="install-help-choices">
              {CHOICES.map((c) => (
                <button key={c} type="button" onClick={() => setGuide(c)} className={`chip ${guide === c ? "chip-on" : ""}`} data-testid={`install-help-choice-${c}`}>{GUIDES[c].label}</button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
